/**
 * Playwright trace snapshot reconstruction.
 *
 * Lifted from packages/canary-cli/src/trace/snapshot-reconstructor.ts so the
 * diagnostic materializer (apps/api) and the canary CLI use one reconstructor.
 *
 * Heavy: launches a Playwright browser to drive the trace viewer's snapshot
 * service worker. Typical reconstruction is 10-30s for a full run. Only call
 * from contexts that can absorb that latency (the diagnostic job has a 5-min
 * timeout, which is comfortable).
 */

import { chromium } from 'playwright';
import type { Browser, Page } from 'playwright';
import path from 'node:path';
import fs from 'node:fs';
import os from 'node:os';
import { parseTraceActions, filterMutationActions } from './trace-action-parser.js';
import type { ReconstructOptions, SnapshotResult, TraceAction, TraceSnapshotPhase } from './types.js';


interface TraceViewerServer {
  urlPrefix(kind?: 'precise'): string;
  stop(): Promise<void>;
}

export type TraceViewerAllowedFileRoots = (() => string[]) &
  Pick<ReadonlyArray<string>, 'some'>;

export function createTraceViewerAllowedFileRoots(
  roots: string[]
): TraceViewerAllowedFileRoots {
  const stableRoots = roots.slice();
  return Object.assign(() => stableRoots, {
    some: stableRoots.some.bind(stableRoots),
  });
}

export function snapshotNameForPhase(
  action: TraceAction,
  phase: TraceSnapshotPhase
): string | undefined {
  switch (phase) {
    case 'before':
      return action.beforeSnapshot;
    case 'input':
      return action.inputSnapshot;
    case 'after':
      return action.afterSnapshot;
  }
}

/**
 * Reconstruct AI semantic snapshots from a Playwright trace.zip.
 *
 * Flow:
 * 1. Start Playwright's trace viewer server (serves reconstructed HTML via service worker)
 * 2. Open a headless browser
 * 3. Initialize the service worker with the trace data
 * 4. For each action, navigate to the snapshot URL and call ariaSnapshot()
 */
export async function reconstructSnapshots(
  options: ReconstructOptions
): Promise<SnapshotResult[]> {
  const { traceZipPath, filter, includeScreenshots, includeHtml, actionIndex, onProgress } = options;
  const log = onProgress ?? (() => {});

  const resolvedPath = path.resolve(traceZipPath);
  if (!fs.existsSync(resolvedPath)) {
    throw new Error(`Trace file not found: ${resolvedPath}`);
  }

  log('Parsing trace actions...');
  const allActions = parseTraceActions(resolvedPath);
  const actions = filterMutationActions(allActions);
  log(`Found ${actions.length} mutation actions (${allActions.length} total)`);

  if (actions.length === 0) {
    return [];
  }

  if (actionIndex !== undefined && !actions[actionIndex]) {
    throw new Error(`Action index ${actionIndex} out of range (0-${actions.length - 1})`);
  }
  const targetActions = actionIndex !== undefined ? [actions[actionIndex]!] : actions;

  log('Starting trace viewer server...');
  // Resolving + starting the trace viewer flakes intermittently in the worker
  // fleet (prod: ~7% of reconstructions throw "Could not load Playwright trace
  // viewer", interleaved with successes on the same image). Retry a couple of
  // times with a short backoff before giving up so a transient resolve/start
  // failure doesn't fail the whole reconstruction.
  // playwright-core >= 1.60 gates the `/trace/file?path=` route behind
  // `allowedFileRoots` (default `[process.cwd()]`) and refuses any trace
  // outside them — so a trace materialized to a temp dir (the normal case)
  // is silently refused. Allow the trace's own directory.
  const server: TraceViewerServer = await withTraceViewerRetry(
    async () => {
      const { startTraceViewerServer } = await loadTraceViewer();
      const allowedFileRoots = createTraceViewerAllowedFileRoots([path.dirname(resolvedPath)]);
      return startTraceViewerServer({
        host: '127.0.0.1',
        allowedFileRoots,
      });
    },
    { onAttemptError: (msg, attempt) => log(`Trace viewer start failed (attempt ${attempt}): ${msg}`) }
  );
  const serverUrl = server.urlPrefix('precise');

  // The trace viewer server serves local files via /trace/file?path=<path>.
  // The service worker loads traces from this URL.
  const traceFileUrl = `${serverUrl}/trace/file?path=${encodeURIComponent(resolvedPath)}`;

  let browser: Browser | undefined;
  try {
    log('Launching headless browser...');
    browser = await chromium.launch({ headless: true });
    const context = await browser.newContext();

    // Initialize the service worker with our trace, then close the bootstrap page
    log('Initializing service worker...');
    const initPage = await context.newPage();
    const snapshotHtmlUrl = `${serverUrl}/trace/snapshot.html?trace=${encodeURIComponent(traceFileUrl)}&r=about:blank`;
    await initPage.goto(snapshotHtmlUrl, { waitUntil: 'load', timeout: 15_000 });
    await waitForTraceServiceWorker(initPage);
    await initPage.close();
    log('Service worker ready');

    const snapshotPage = await context.newPage();

    const results: SnapshotResult[] = [];

    for (let i = 0; i < targetActions.length; i++) {
      const action = targetActions[i]!;
      const globalIndex = actionIndex ?? i;
      const phases: TraceSnapshotPhase[] =
        filter === 'both' ? ['before', 'after'] : [filter];

      for (const phase of phases) {
        const snapshotName = snapshotNameForPhase(action, phase);

        if (!snapshotName) continue;

        log(`[${globalIndex + 1}/${actions.length}] ${action.title} (${phase})`);

        try {
          const snapshot = await captureSnapshot(
            snapshotPage,
            serverUrl,
            traceFileUrl,
            action.pageId,
            snapshotName,
            includeHtml
          );

          let screenshotBuffer: Buffer | undefined;
          if (includeScreenshots) {
            screenshotBuffer = await snapshotPage.screenshot({ type: 'png' }) as Buffer;
          }

          results.push({
            actionIndex: globalIndex,
            action,
            phase,
            yaml: snapshot.yaml,
            html: snapshot.html,
            screenshotBuffer,
          });
        } catch (err) {
          log(`  ⚠ Failed to capture snapshot: ${err instanceof Error ? err.message : err}`);
          results.push({
            actionIndex: globalIndex,
            action,
            phase,
            yaml: `[Error: Failed to reconstruct snapshot - ${err instanceof Error ? err.message : 'unknown error'}]`,
          });
        }
      }
    }

    return results;
  } finally {
    if (browser) await browser.close().catch(() => {});
    await server.stop().catch(() => {});
  }
}

async function waitForTraceServiceWorker(page: Page): Promise<void> {
  await page.waitForFunction(
    async () => {
      if (!('serviceWorker' in navigator)) return false;
      if (navigator.serviceWorker.controller) return true;

      const registration = await navigator.serviceWorker.ready.catch(() => null);
      if (!registration) return false;

      if (navigator.serviceWorker.controller) return true;
      if (!registration.active) return false;

      // The trace viewer's first controlled page can require a reload after
      // installation. The caller only needs the worker installed before it opens
      // snapshot URLs in subsequent pages.
      return true;
    },
    null,
    { timeout: 10_000 }
  );
}

/**
 * Navigate to a specific snapshot and capture the AI semantic YAML.
 */
async function captureSnapshot(
  page: Page,
  serverUrl: string,
  traceFileUrl: string,
  pageId: string,
  snapshotName: string,
  includeHtml: boolean
): Promise<{ yaml: string; html?: string }> {
  // The SW routes /snapshot/<pageId> and reads the trace from the `trace` query param.
  const snapshotUrl =
    `${serverUrl}/trace/snapshot/${pageId}` +
    `?trace=${encodeURIComponent(traceFileUrl)}` +
    `&name=${encodeURIComponent(snapshotName)}`;

  await page.goto(snapshotUrl, { waitUntil: 'load', timeout: 15_000 });

  const yaml = await page.ariaSnapshot({ mode: 'ai' });
  const html = includeHtml ? await page.content() : undefined;

  return { yaml, html };
}

/**
 * Download a remote trace URL to a temp file.
 */
export async function downloadTrace(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download trace: ${response.status} ${response.statusText}`);
  }
  const buffer = Buffer.from(await response.arrayBuffer());
  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'canary-trace-'));
  const tmpPath = path.join(tmpDir, 'trace.zip');
  fs.writeFileSync(tmpPath, buffer);
  return tmpPath;
}

/**
 * Clean up a downloaded temp trace.
 */
export function cleanupTrace(tmpPath: string): void {
  try {
    fs.rmSync(path.dirname(tmpPath), { recursive: true, force: true });
  } catch {
    // Best-effort cleanup
  }
}

/**
 * Dynamically load Playwright's trace viewer module.
 *
 * Uses the playwright-core shipped alongside the `playwright` package.
 * Bun's hoisted node_modules layout requires resolving through the
 * playwright-core package.json location.
 */
/**
 * Resolve Playwright's trace-viewer server factory across playwright-core
 * layouts. Exported for a regression test that fails loudly if a playwright
 * upgrade relocates the entry again (the 1.60 bump silently broke this and
 * gapped every cache-optimizer action for weeks).
 */
/** Memoized once a strategy succeeds — resolution is process-stable, so a later
 *  call can't suddenly find a layout the first didn't, and re-resolving on every
 *  reconstruction just adds flake surface. */
let cachedTraceViewer: { startTraceViewerServer: TraceViewerServerFactory } | null = null;

export async function loadTraceViewer(): Promise<{
  startTraceViewerServer: TraceViewerServerFactory;
}> {
  if (cachedTraceViewer) return cachedTraceViewer;

  const { createRequire } = await import('node:module');
  const require = createRequire(import.meta.url);
  // Capture each strategy's failure so the final throw names WHY (all three were
  // silently swallowed before — a load failure was undiagnosable in prod logs).
  const failures: string[] = [];

  // playwright-core >= ~1.58 bundles the entire `lib/server` tree into
  // `lib/coreBundle.js` and removed the standalone
  // `lib/server/trace/viewer/traceViewer` module (and blocks deep subpath
  // imports via its exports map). The trace viewer server now lives at
  // `coreBundle.server.startTraceViewerServer`. Try the bundled location
  // first so reconstruction keeps working after the 1.60 upgrade — without
  // this, every `materialize_substrate` throws "Could not load Playwright
  // trace viewer" and the cache optimizer gaps every action.
  try {
    const pwCorePackage = require.resolve('playwright-core/package.json');
    const pwCoreDir = path.dirname(pwCorePackage);
    const coreBundle = require(path.join(pwCoreDir, 'lib', 'coreBundle.js')) as {
      server?: { startTraceViewerServer?: TraceViewerServerFactory };
    };
    const factory = coreBundle?.server?.startTraceViewerServer;
    if (typeof factory === 'function') {
      cachedTraceViewer = { startTraceViewerServer: factory };
      return cachedTraceViewer;
    }
    failures.push('coreBundle: startTraceViewerServer not a function');
  } catch (e) {
    failures.push(`coreBundle: ${e instanceof Error ? e.message : String(e)}`);
  }

  // Legacy layout (playwright-core <= ~1.57): standalone traceViewer module.
  try {
    const pwCorePackage = require.resolve('playwright-core/package.json');
    const pwCoreDir = path.dirname(pwCorePackage);
    const mod = require(path.join(pwCoreDir, 'lib', 'server', 'trace', 'viewer', 'traceViewer')) as {
      startTraceViewerServer?: TraceViewerServerFactory;
    };
    // Validate the export before caching — a module that resolves but lacks the
    // factory must NOT be memoized as a broken loader for the process lifetime.
    if (typeof mod?.startTraceViewerServer === 'function') {
      cachedTraceViewer = { startTraceViewerServer: mod.startTraceViewerServer };
      return cachedTraceViewer;
    }
    failures.push('legacy: startTraceViewerServer not a function');
  } catch (e) {
    failures.push(`legacy: ${e instanceof Error ? e.message : String(e)}`);
  }

  try {
    const pwEntry = require.resolve('playwright');
    const pwDir = path.dirname(pwEntry);
    // playwright ships alongside playwright-core in the same node_modules scope
    const pwCoreTraceViewer = path.join(pwDir, '..', 'playwright-core', 'lib', 'server', 'trace', 'viewer', 'traceViewer');
    const mod = require(pwCoreTraceViewer) as { startTraceViewerServer?: TraceViewerServerFactory };
    if (typeof mod?.startTraceViewerServer === 'function') {
      cachedTraceViewer = { startTraceViewerServer: mod.startTraceViewerServer };
      return cachedTraceViewer;
    }
    failures.push('nested: startTraceViewerServer not a function');
  } catch (e) {
    failures.push(`nested: ${e instanceof Error ? e.message : String(e)}`);
  }

  throw new Error(
    `Could not load Playwright trace viewer. Make sure playwright is installed. Tried: ${failures.join(' | ')}`
  );
}

/**
 * Bounded retry with short backoff for the flaky trace-viewer load+start.
 * Exported as a pure combinator so the retry policy is unit-testable without a
 * real browser. `delayMs` is injectable so tests don't wait.
 */
export async function withTraceViewerRetry<T>(
  start: () => Promise<T>,
  opts: { attempts?: number; onAttemptError?: (msg: string, attempt: number) => void; delayMs?: (attempt: number) => number } = {}
): Promise<T> {
  const attempts = opts.attempts ?? 3;
  const delayMs = opts.delayMs ?? ((i) => 200 * (i + 1));
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await start();
    } catch (e) {
      lastErr = e;
      opts.onAttemptError?.(e instanceof Error ? e.message : String(e), i + 1);
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs(i)));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

type TraceViewerServerFactory = (options?: {
  host?: string;
  port?: number;
  /** playwright-core >= 1.60: file roots the `/trace/file` route may serve. */
  allowedFileRoots?: TraceViewerAllowedFileRoots;
}) => Promise<TraceViewerServer>;
